import type { QuizAnswers } from "@/src/components/OnboardingWizard/types";
import { GOAL_OPTIONS } from "@/src/components/OnboardingWizard/quizStepsConfig";
import type { GoalOptionDefinition } from "@/src/components/OnboardingWizard/quizStepsConfig";

export type GoalHookId =
  "million" | "retirement" | "home" | "emergency" | "idlecash" | "debt";

export interface ScoredGoalHook {
  hookId: GoalHookId;
  score: number;
  option: GoalOptionDefinition;
}

const BACKFILL_ORDER: GoalHookId[] = [
  "million",
  "retirement",
  "home",
  "idlecash",
];

const TIEBREAK_ORDER: GoalHookId[] = [
  "emergency",
  "debt",
  "idlecash",
  "home",
  "million",
  "retirement",
];

const YOUNG_AGE_RANGES = new Set(["18-24", "25-34", "35-44"]);
const OLD_AGE_RANGES = new Set(["45-54", "55+"]);

/** v1 effective tax placeholder (PRD external → constant). */
const effectiveTaxRate = (annualIncome: number): number => {
  if (annualIncome >= 150_000) {
    return 0.32;
  }
  if (annualIncome >= 60_000) {
    return 0.25;
  }
  return 0.2;
};

const getGoalOption = (hookId: GoalHookId): GoalOptionDefinition => {
  const idMap: Record<GoalHookId, string> = {
    million: "million",
    retirement: "retirement",
    home: "home",
    emergency: "runway",
    idlecash: "idle-cash",
    debt: "debt-free",
  };
  const option = GOAL_OPTIONS.find((g) => g.id === idMap[hookId]);
  if (!option) {
    throw new Error(`Missing goal option for hook ${hookId}`);
  }
  return option;
};

const scoreHooks = (answers: QuizAnswers): Record<GoalHookId, number> => {
  const income = answers.income;
  const expenseM = answers.expenses;
  const cash = answers.cash;
  const owe = answers.debt;
  const invested = answers.invest;

  const incomeNet = income * (1 - effectiveTaxRate(income));
  const surplus = incomeNet - expenseM * 12 - owe / 3;
  const runwayMonths = expenseM > 0 ? cash / expenseM : 0;
  const debtLoad = income > 0 ? owe / income : 0;
  const idleCash = Math.max(cash - expenseM * 6, 0);

  let debtScore = 0;
  if (owe <= 0) {
    debtScore = 0;
  } else if (debtLoad >= 0.4) {
    debtScore = 90;
  } else if (debtLoad >= 0.15) {
    debtScore = 45;
  } else {
    debtScore = 5;
  }

  let emergencyScore = 10;
  if (runwayMonths < 3) {
    emergencyScore = 95;
  } else if (runwayMonths <= 9) {
    emergencyScore = 35;
  }

  let idlecashScore = 0;
  if (idleCash > 30_000) {
    idlecashScore = 70;
  } else if (idleCash >= 10_000) {
    idlecashScore = 45;
  }

  const compoundBase = surplus > 0 && invested > 0 ? 40 : 0;
  const millionScore =
    compoundBase + (YOUNG_AGE_RANGES.has(answers.age) ? 20 : 0);
  const retirementScore =
    compoundBase + (OLD_AGE_RANGES.has(answers.age) ? 20 : 0);

  let homeScore = 0;
  if (cash > 20_000 && income > 60_000 && debtLoad < 0.4) {
    homeScore = 50;
  } else if (debtLoad >= 0.4) {
    homeScore = 25;
  }

  return {
    debt: debtScore,
    emergency: emergencyScore,
    idlecash: idlecashScore,
    million: surplus > 0 && invested > 0 ? millionScore : 0,
    retirement: surplus > 0 && invested > 0 ? retirementScore : 0,
    home: homeScore,
  };
};

const compareHooks = (
  a: GoalHookId,
  b: GoalHookId,
  scores: Record<GoalHookId, number>,
) => {
  const diff = scores[b] - scores[a];
  if (diff !== 0) {
    return diff;
  }
  return TIEBREAK_ORDER.indexOf(a) - TIEBREAK_ORDER.indexOf(b);
};

/**
 * PRD §4.1 goal matching engine — top-4 with backfill.
 */
export const scoreOnboardingGoals = (
  answers: QuizAnswers,
): ScoredGoalHook[] => {
  const scores = scoreHooks(answers);
  const positive = (Object.keys(scores) as GoalHookId[])
    .filter((hookId) => scores[hookId] > 0)
    .sort((a, b) => compareHooks(a, b, scores))
    .map((hookId) => ({
      hookId,
      score: scores[hookId],
      option: getGoalOption(hookId),
    }));

  if (positive.length >= 4) {
    return positive.slice(0, 4);
  }

  const selectedIds = new Set(positive.map((item) => item.hookId));
  const backfilled = [...positive];

  for (const hookId of BACKFILL_ORDER) {
    if (backfilled.length >= 4) {
      break;
    }
    if (selectedIds.has(hookId)) {
      continue;
    }
    selectedIds.add(hookId);
    backfilled.push({
      hookId,
      score: scores[hookId],
      option: getGoalOption(hookId),
    });
  }

  return backfilled.slice(0, 4);
};
